/**
 * chat.service.ts
 * ---------------
 * Module 12 — Analysis Assistant (frontend API service).
 *
 * Endpoints used:
 *   POST /api/v1/sessions/{id}/chat         — full response (non-streaming)
 *   POST /api/v1/sessions/{id}/chat/stream  — SSE streaming response
 */

import { Injectable }                    from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError }        from 'rxjs';
import { catchError }                    from 'rxjs/operators';
import { MsalService }                   from '@azure/msal-angular';
import { environment }                   from '../../../environments/environment';

// ---------------------------------------------------------------------------
// DTOs
// ---------------------------------------------------------------------------

export interface ChatHistoryMessage {
  role: 'user' | 'assistant';
  text: string;
}

export interface ChatRequest {
  query:   string;
  history: ChatHistoryMessage[];
}

export interface ChatResponse {
  response: string;
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

@Injectable({ providedIn: 'root' })
export class ChatService {

  private readonly _base =
    `${environment.apiConfig.uri.replace(/\/$/, '')}/api/v1/sessions`;

  constructor(
    private readonly http:  HttpClient,
    private readonly msal:  MsalService,
  ) {}

  /**
   * Stream a chat response using SSE (Server-Sent Events).
   * Emits text delta strings one at a time as the LLM produces them.
   * Completes when the server sends `data: [DONE]`.
   *
   * @param sessionId  The active session UUID.
   * @param query      The user's latest message.
   * @param history    Prior conversation turns.
   */
  streamMessage(
    sessionId: string,
    query:     string,
    history:   ChatHistoryMessage[],
  ): Observable<string> {
    return new Observable<string>(observer => {
      const controller = new AbortController();

      // Acquire the MSAL Bearer token, then open the fetch stream
      const accounts = this.msal.instance.getAllAccounts();
      const tokenRequest = {
        scopes:  environment.apiConfig.scopes,
        account: accounts[0] ?? undefined,
      };

      this.msal.instance.acquireTokenSilent(tokenRequest)
        .then(tokenResult => {
          const headers: Record<string, string> = {
            'Content-Type':  'application/json',
            'Authorization': `Bearer ${tokenResult.accessToken}`,
          };

          return fetch(`${this._base}/${sessionId}/chat/stream`, {
            method:  'POST',
            headers,
            body:    JSON.stringify({ query, history } satisfies ChatRequest),
            signal:  controller.signal,
          });
        })
        .then(async response => {
          if (!response.ok) {
            const errBody = await response.text().catch(() => '');
            observer.error(new Error(`HTTP ${response.status}: ${errBody}`));
            return;
          }

          const reader  = response.body!.getReader();
          const decoder = new TextDecoder();
          let   buffer  = '';

          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });

            // SSE lines are separated by \n\n — process all complete events
            const parts = buffer.split('\n\n');
            buffer = parts.pop() ?? '';   // keep any incomplete trailing chunk

            for (const part of parts) {
              for (const line of part.split('\n')) {
                if (!line.startsWith('data: ')) continue;
                const payload = line.slice(6).trim();
                if (payload === '[DONE]') {
                  observer.complete();
                  return;
                }
                try {
                  const parsed = JSON.parse(payload) as { text: string };
                  if (parsed.text) observer.next(parsed.text);
                } catch { /* ignore malformed lines */ }
              }
            }
          }
          observer.complete();
        })
        .catch(err => {
          if ((err as Error).name !== 'AbortError') {
            observer.error(err);
          }
        });

      // Teardown: abort the fetch when the subscriber unsubscribes
      return () => controller.abort();
    });
  }

  /**
   * Non-streaming fallback — returns the full response at once.
   */
  sendMessage(
    sessionId: string,
    query:     string,
    history:   ChatHistoryMessage[],
  ): Observable<ChatResponse> {
    const body: ChatRequest = { query, history };
    return this.http
      .post<ChatResponse>(`${this._base}/${sessionId}/chat`, body)
      .pipe(catchError(this._handleError));
  }

  private _handleError(err: HttpErrorResponse): Observable<never> {
    const msg =
      err.error?.detail ??
      err.error?.message ??
      err.message ??
      'Chat service error.';
    return throwError(() => new Error(msg));
  }
}
